import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetPreSignedUrlForUpload } from '@/lib/api/hooks/use-storage';
import { ModuleName } from '@/constant/modules.constants';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Loader2, Upload, Check } from 'lucide-react';
import type { OrganizationLetterheadInsertInput } from '../types/org-letterheads.types';

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';

interface LetterheadUploadDialogProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onUpload: (input: OrganizationLetterheadInsertInput) => Promise<void>;
}

type Step = 'upload' | 'configure';
type ProcessingStatus = 'idle' | 'uploading' | 'processing' | 'complete';

export const LetterheadUploadDialog = ({
  open,
  onClose,
  orgId,
  onUpload,
}: LetterheadUploadDialogProps) => {
  const { t } = useTranslation();
  const { mutateAsync: getPreSignedUrl } = useGetPreSignedUrlForUpload();

  const [step, setStep] = useState<Step>('upload');
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileKey, setFileKey] = useState<string>('');
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(
    null
  );
  const [letterheadName, setLetterheadName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Content area state
  const [contentArea, setContentArea] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  // Editor drag state — tracks both axes so we can resize via north/south
  // AND east/west handles.
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialArea, setInitialArea] = useState<typeof contentArea>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Calculate scale for preview — viewport-aware so the rendered page
  // doesn't overflow on phones. Recomputes on window resize.
  useEffect(() => {
    if (!imageDimensions) return;
    const computeScale = () => {
      const isMobile = window.innerWidth < 768;
      // On desktop the settings panel takes 280px + 24px gap; mobile is full-width.
      const horizontalChrome = isMobile ? 80 : 420;
      const verticalChrome = isMobile ? 320 : 240;
      const maxWidth = Math.min(isMobile ? 360 : 480, window.innerWidth - horizontalChrome);
      const maxHeight = Math.min(isMobile ? 360 : 520, window.innerHeight - verticalChrome);
      const scaleX = maxWidth / imageDimensions.width;
      const scaleY = maxHeight / imageDimensions.height;
      setScale(Math.min(scaleX, scaleY, 1));
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, [imageDimensions]);

  const reset = () => {
    setStep('upload');
    setProcessingStatus('idle');
    setFile(null);
    setFileUrl(null);
    setFileKey('');
    setImageDataUrl(null);
    setImageDimensions(null);
    setLetterheadName('');
    setIsDefault(false);
    setIsSaving(false);
    setError(null);
    setContentArea(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = useCallback(
    async (selectedFile: File) => {
      setFile(selectedFile);
      setLetterheadName(selectedFile.name.replace(/\.[^/.]+$/, ''));
      setProcessingStatus('uploading');
      setError(null);

      try {
        // Get pre-signed URL and upload
        const data = await getPreSignedUrl({
          name: selectedFile.name,
          projectKey,
          itemId: '',
          metaData: '',
          accessModifier: 'Public',
          configurationName: 'Default',
          parentDirectoryId: '',
          tags: '',
          moduleName: ModuleName.DefaultConstruct,
        });

        if (!data.isSuccess || !data.uploadUrl) {
          throw new Error('Failed to get upload URL');
        }

        await fetch(data.uploadUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: {
            'Content-Type': selectedFile.type,
            'x-ms-blob-type': 'BlockBlob',
          },
        });

        const uploadedUrl = data.uploadUrl.split('?')[0];
        setFileUrl(uploadedUrl);
        setFileKey(data.fileId || '');

        // Process image for preview
        setProcessingStatus('processing');

        // Default content area uses percentages of the image — 10% left/
        // right and 15% top/bottom — so margins scale sensibly regardless
        // of source resolution. A hardcoded 50px margin on a 2480px-wide
        // image is only 2% — way too tight for a print letterhead.
        const buildDefaultArea = (w: number, h: number) => ({
          x: w * 0.1,
          y: h * 0.15,
          width: w * 0.8,
          height: h * 0.7,
        });

        if (selectedFile.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            setImageDataUrl(dataUrl);

            const img = new Image();
            img.onload = () => {
              const dims = { width: img.width, height: img.height };
              setImageDimensions(dims);
              setContentArea(buildDefaultArea(dims.width, dims.height));
              setProcessingStatus('complete');
              setStep('configure');
            };
            img.src = dataUrl;
          };
          reader.readAsDataURL(selectedFile);
        } else {
          // For non-image files (PDF), use default dimensions
          const defaultWidth = 612;
          const defaultHeight = 792;
          setImageDimensions({ width: defaultWidth, height: defaultHeight });
          setContentArea(buildDefaultArea(defaultWidth, defaultHeight));
          setProcessingStatus('complete');
          setStep('configure');
        }
      } catch (err) {
        console.error('Error processing file:', err);
        setError(
          err instanceof Error ? err.message : t('ORGSETTINGS_LETTERHEAD_ERRORS_PROCESSFAILED')
        );
        setProcessingStatus('idle');
      }
    },
    [getPreSignedUrl, t]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) processFile(selectedFile);
    },
    [processFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) processFile(droppedFile);
    },
    [processFile]
  );

  const handleSave = async () => {
    if (!file || !fileUrl || !contentArea || !imageDimensions || !letterheadName.trim()) {
      setError(t('ORGSETTINGS_LETTERHEAD_ERRORS_MISSINGNAME'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Convert from image pixels to PDF points before persisting.
      // @react-pdf/renderer interprets PageWidth/PageHeight as points
      // (1/72 inch). If we save raw 2480×3509 pixels, the renderer later
      // tries to rasterize a 34"×49" surface and Chrome flags the tab
      // "Page Unresponsive." Detect A4 vs LETTER from aspect ratio so the
      // letterhead image isn't distorted in either the editor preview or
      // the rendered PDF.
      const aspect = imageDimensions.width / imageDimensions.height;
      const A4_RATIO = 595 / 842;
      const LETTER_RATIO = 612 / 792;
      const isA4 = Math.abs(aspect - A4_RATIO) < Math.abs(aspect - LETTER_RATIO);
      const pdfPageWidth = isA4 ? 595 : 612;
      const pdfPageHeight = isA4 ? 842 : 792;
      const scaleX = pdfPageWidth / imageDimensions.width;
      const scaleY = pdfPageHeight / imageDimensions.height;

      await onUpload({
        OrganizationId: orgId,
        Name: letterheadName.trim(),
        FileUrl: fileUrl,
        FileKey: fileKey,
        FileName: file.name,
        FileType: file.type,
        PageWidth: pdfPageWidth,
        PageHeight: pdfPageHeight,
        ContentAreaX: Math.round(contentArea.x * scaleX),
        ContentAreaY: Math.round(contentArea.y * scaleY),
        ContentAreaWidth: Math.round(contentArea.width * scaleX),
        ContentAreaHeight: Math.round(contentArea.height * scaleY),
        IsDefault: isDefault,
      });

      handleClose();
    } catch (err) {
      console.error('Error saving letterhead:', err);
      setError(
        err instanceof Error ? err.message : t('ORGSETTINGS_LETTERHEAD_ERRORS_SAVEFAILED')
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Content area drag handling — accepts both mouse and touch so the
  // resize handles work on phones and tablets. Tracks both axes so the
  // east/west handles can adjust horizontal margins too.
  const startDrag = (clientX: number, clientY: number, handle: string) => {
    setIsDragging(true);
    setDragHandle(handle);
    setDragStart({ x: clientX, y: clientY });
    setInitialArea(contentArea);
  };

  const handleMouseDown = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    startDrag(e.clientX, e.clientY, handle);
  };

  const handleTouchStart = (e: React.TouchEvent, handle: string) => {
    e.stopPropagation();
    startDrag(e.touches[0].clientX, e.touches[0].clientY, handle);
  };

  useEffect(() => {
    if (!isDragging || !initialArea || !imageDimensions) return;

    const moveTo = (clientX: number, clientY: number) => {
      const deltaX = (clientX - dragStart.x) / scale;
      const deltaY = (clientY - dragStart.y) / scale;
      const newArea = { ...initialArea };

      if (dragHandle === 'n') {
        const newY = Math.max(
          0,
          Math.min(initialArea.y + deltaY, initialArea.y + initialArea.height - 50)
        );
        newArea.height = initialArea.y + initialArea.height - newY;
        newArea.y = newY;
      } else if (dragHandle === 's') {
        newArea.height = Math.max(
          50,
          Math.min(initialArea.height + deltaY, imageDimensions.height - initialArea.y)
        );
      } else if (dragHandle === 'w') {
        const newX = Math.max(
          0,
          Math.min(initialArea.x + deltaX, initialArea.x + initialArea.width - 50)
        );
        newArea.width = initialArea.x + initialArea.width - newX;
        newArea.x = newX;
      } else if (dragHandle === 'e') {
        newArea.width = Math.max(
          50,
          Math.min(initialArea.width + deltaX, imageDimensions.width - initialArea.x)
        );
      }

      setContentArea(newArea);
    };

    const handleMouseMove = (e: MouseEvent) => moveTo(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      moveTo(e.touches[0].clientX, e.touches[0].clientY);
    };
    const handleEnd = () => {
      setIsDragging(false);
      setDragHandle(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDragging, dragHandle, dragStart, initialArea, scale, imageDimensions]);

  const scaledArea =
    contentArea && imageDimensions
      ? {
          x: contentArea.x * scale,
          y: contentArea.y * scale,
          width: contentArea.width * scale,
          height: contentArea.height * scale,
        }
      : null;

  const getStatusMessage = () => {
    switch (processingStatus) {
      case 'uploading':
        return t('ORGSETTINGS_LETTERHEAD_STATUS_UPLOADING');
      case 'processing':
        return t('ORGSETTINGS_LETTERHEAD_STATUS_PROCESSING');
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={cn(
          'w-[95vw] max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-2xl',
          step === 'configure' ? 'max-w-4xl!' : 'max-w-lg'
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {step === 'upload'
              ? t('ORGSETTINGS_LETTERHEAD_DIALOGTITLEUPLOAD')
              : t('ORGSETTINGS_LETTERHEAD_DIALOGTITLECONFIGURE')}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload'
              ? t('ORGSETTINGS_LETTERHEAD_DIALOGDESCRIPTIONUPLOAD')
              : t('ORGSETTINGS_LETTERHEAD_DIALOGDESCRIPTIONCONFIGURE')}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="p-3 rounded-md bg-crimson/10 text-crimson text-sm">{error}</div>
        )}

        {step === 'upload' && (
          <div className="space-y-4">
            {processingStatus !== 'idle' ? (
              <div className="border-2 border-selise-blue/30 rounded-lg p-6 md:p-8 bg-selise-blue/5">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <Loader2 className="w-10 h-10 md:w-12 md:h-12 text-selise-blue animate-spin" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-[hsl(var(--fg))]">{getStatusMessage()}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('ORGSETTINGS_LETTERHEAD_PROCESSINGHINT')}
                    </p>
                  </div>
                  {/* Progress steps */}
                  <div className="flex items-center gap-2 mt-2">
                    <div
                      className={`flex items-center gap-1.5 text-xs ${
                        processingStatus === 'uploading'
                          ? 'text-selise-blue'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {processingStatus !== 'uploading' ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-current" />
                      )}
                      {t('ORGSETTINGS_LETTERHEAD_STEPS_UPLOAD')}
                    </div>
                    <div className="w-4 h-px bg-border" />
                    <div
                      className={`flex items-center gap-1.5 text-xs ${
                        processingStatus === 'processing'
                          ? 'text-selise-blue'
                          : processingStatus === 'complete'
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground/50'
                      }`}
                    >
                      {processingStatus === 'complete' ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : processingStatus === 'processing' ? (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-current" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-current/50" />
                      )}
                      {t('ORGSETTINGS_LETTERHEAD_STEPS_PROCESS')}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-border p-6 md:p-8 transition-colors hover:border-selise-blue/50 hover:bg-selise-blue/5 cursor-pointer"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => document.getElementById('letterhead-file')?.click()}
              >
                <Upload className="h-10 w-10 md:h-12 md:w-12 text-selise-blue" />
                <div className="text-center">
                  <p className="font-medium text-[hsl(var(--fg))]">
                    {t('ORGSETTINGS_LETTERHEAD_DROPZONE_LABEL')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('ORGSETTINGS_LETTERHEAD_DROPZONE_ALLOWEDCONTENT')}
                  </p>
                </div>
                <input
                  id="letterhead-file"
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            )}
          </div>
        )}

        {step === 'configure' && contentArea && imageDimensions && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr,280px] gap-4 md:gap-6">
            {/* Preview with content area editor — order-2 on mobile so the
                form fields land at the top of the scroll viewport. */}
            <div className="space-y-3 order-2 md:order-1">
              <div className="text-sm text-muted-foreground">
                {t('ORGSETTINGS_LETTERHEAD_EDITOR_HELPER')}
              </div>
              <div className="relative border rounded-lg bg-muted/30 p-2 md:p-4 flex items-center justify-center min-h-[260px] md:min-h-[400px]">
                <div
                  ref={imageContainerRef}
                  className="relative shadow-lg"
                  style={{
                    width: imageDimensions.width * scale,
                    height: imageDimensions.height * scale,
                  }}
                >
                  {imageDataUrl ? (
                    <img
                      src={imageDataUrl}
                      alt={t('ORGSETTINGS_LETTERHEAD_PREVIEWALT')}
                      className="block rounded"
                      style={{
                        width: imageDimensions.width * scale,
                        height: imageDimensions.height * scale,
                      }}
                      draggable={false}
                    />
                  ) : (
                    <div
                      className="bg-white rounded flex items-center justify-center"
                      style={{
                        width: imageDimensions.width * scale,
                        height: imageDimensions.height * scale,
                      }}
                    >
                      <p className="text-sm text-muted-foreground">
                        {t('ORGSETTINGS_LETTERHEAD_PREVIEWALT')}
                      </p>
                    </div>
                  )}

                  {/* Content area overlay */}
                  {scaledArea && (
                    <div
                      className="absolute border-2 border-selise-blue bg-selise-blue/10 pointer-events-none"
                      style={{
                        left: scaledArea.x,
                        top: scaledArea.y,
                        width: scaledArea.width,
                        height: scaledArea.height,
                      }}
                    >
                      {/* Top handle */}
                      <div
                        className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-selise-blue rounded cursor-ns-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                        onMouseDown={(e) => handleMouseDown(e, 'n')}
                        onTouchStart={(e) => handleTouchStart(e, 'n')}
                      >
                        <div className="w-10 h-0.5 bg-white/80 rounded" />
                      </div>
                      {/* Bottom handle */}
                      <div
                        className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-selise-blue rounded cursor-ns-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                        onMouseDown={(e) => handleMouseDown(e, 's')}
                        onTouchStart={(e) => handleTouchStart(e, 's')}
                      >
                        <div className="w-10 h-0.5 bg-white/80 rounded" />
                      </div>
                      {/* Left handle */}
                      <div
                        className="absolute top-1/2 -left-3 -translate-y-1/2 w-6 h-20 bg-selise-blue rounded cursor-ew-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                        onMouseDown={(e) => handleMouseDown(e, 'w')}
                        onTouchStart={(e) => handleTouchStart(e, 'w')}
                      >
                        <div className="h-10 w-0.5 bg-white/80 rounded" />
                      </div>
                      {/* Right handle */}
                      <div
                        className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-20 bg-selise-blue rounded cursor-ew-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                        onMouseDown={(e) => handleMouseDown(e, 'e')}
                        onTouchStart={(e) => handleTouchStart(e, 'e')}
                      >
                        <div className="h-10 w-0.5 bg-white/80 rounded" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Settings panel — order-1 on mobile so the form is at the top. */}
            <div className="space-y-5 order-1 md:order-2">
              <div className="space-y-2">
                <Label htmlFor="letterheadName">{t('ORGSETTINGS_LETTERHEAD_NAMELABEL')}</Label>
                <Input
                  id="letterheadName"
                  value={letterheadName}
                  onChange={(e) => setLetterheadName(e.target.value)}
                  placeholder={t('ORGSETTINGS_LETTERHEAD_NAMEPLACEHOLDER')}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  {t('ORGSETTINGS_LETTERHEAD_NAMEHELP')}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isDefault"
                  checked={isDefault}
                  onCheckedChange={(checked) => setIsDefault(checked === true)}
                />
                <Label htmlFor="isDefault" className="text-sm font-normal cursor-pointer">
                  {t('ORGSETTINGS_LETTERHEAD_DEFAULTLABEL')}
                </Label>
              </div>

              <div className="pt-2 border-t">
                <div className="text-sm font-medium mb-2">
                  {t('ORGSETTINGS_LETTERHEAD_CONTENTAREATITLE')}
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex justify-between">
                    <span>{t('ORGSETTINGS_LETTERHEAD_HEADEREXCLUDED')}</span>
                    <span className="font-mono">{Math.round(contentArea.y)}px</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('ORGSETTINGS_LETTERHEAD_FOOTEREXCLUDED')}</span>
                    <span className="font-mono">
                      {Math.round(imageDimensions.height - contentArea.y - contentArea.height)}px
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-2 md:pt-4">
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving || !letterheadName.trim()}
                  className="w-full"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('ORGSETTINGS_LETTERHEAD_ACTIONS_SAVING')}
                    </>
                  ) : (
                    t('ORGSETTINGS_LETTERHEAD_ACTIONS_SAVE')
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={handleClose} className="w-full">
                  {t('ORGSETTINGS_LETTERHEAD_ACTIONS_CANCEL')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
